import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
} from "@mui/material";

/**
 * Form genérico para los catálogos RBAC (Roles, Módulos, Permisos).
 * Estos 3 entes comparten la misma forma — { name, key, description } —
 * con la diferencia de que la `key` es inmutable después de la creación.
 *
 * Props:
 *  - open, onClose, onSave({ name, key, description })
 *  - entity: existing record or null for create
 *  - labels: { entityName: "Role" | "Module" | "Permission",
 *              nameLabel, keyLabel, keyHelper,
 *              errName, errKey }
 *  - loading
 *
 * El componente NO sabe el shape final del endpoint — solo emite
 * { name, key, description } y el padre lo mapea (roleName/roleKey,
 * moduleName/moduleKey, etc).
 */
const CatalogFormDialog = ({
  open,
  onClose,
  onSave,
  entity = null,
  labels,
  loading = false,
}) => {
  const isEdit = Boolean(entity);

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(entity?.name || "");
      setKey(entity?.key || "");
      setDescription(entity?.description || "");
      setError("");
    }
  }, [open, entity]);

  const handleSubmit = () => {
    if (!name?.trim()) return setError(labels.errName);
    if (!isEdit && !key?.trim()) return setError(labels.errKey);
    onSave({
      name: name.trim(),
      key: key.trim(),
      description: description.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit ${labels.entityName}` : `New ${labels.entityName}`}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label={`${labels.nameLabel} *`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label={`${labels.keyLabel} *`}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            fullWidth
            size="small"
            disabled={isEdit}
            helperText={labels.keyHelper}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={4}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogFormDialog;
